import { getFilename } from "@openanalyst-ai/core/util/path"
import { type Session } from "@openanalyst-ai/sdk/v2/client"
import { pathKey } from "@/utils/path-key"
import openanalyst from "@/assets/openanalyst-project-icon.png"

type SessionStore = {
  session?: Session[]
  path: { directory: string }
}

const compactTimeUnits = [
  { label: "y", ms: 365 * 24 * 60 * 60 * 1000 },
  { label: "mo", ms: 30 * 24 * 60 * 60 * 1000 },
  { label: "w", ms: 7 * 24 * 60 * 60 * 1000 },
  { label: "d", ms: 24 * 60 * 60 * 1000 },
  { label: "h", ms: 60 * 60 * 1000 },
  { label: "m", ms: 60 * 1000 },
]

export const workspaceKey = pathKey
const OPENANALYST_PROJECT_ID = "4b0ea68d7af9a6031a7ffda7ad66e0cb83315750"

export function getProjectAvatarSource(id?: string, icon?: { color?: string; url?: string; override?: string }) {
  if (id === OPENANALYST_PROJECT_ID) return openanalyst
  if (icon?.override) return icon.override
  if (icon?.color) return undefined
  return icon?.url
}

export function compactSessionTime(time: number, now: number) {
  const elapsed = Math.max(0, now - time)
  const unit = compactTimeUnits.find((item) => elapsed >= item.ms)
  if (!unit) return "now"
  return `${Math.floor(elapsed / unit.ms)}${unit.label}`
}

function sortSessions(now: number) {
  const oneMinuteAgo = now - 60 * 1000
  return (a: Session, b: Session) => {
    const aUpdated = a.time.updated ?? a.time.created
    const bUpdated = b.time.updated ?? b.time.created
    const aRecent = aUpdated > oneMinuteAgo
    const bRecent = bUpdated > oneMinuteAgo
    if (aRecent && bRecent) return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
    if (aRecent && !bRecent) return -1
    if (!aRecent && bRecent) return 1
    return bUpdated - aUpdated
  }
}

const isRootVisibleSession = (session: Session, directory: string) =>
  pathKey(session.directory) === pathKey(directory) && !session.parentID && !session.time?.archived

export const roots = (store: SessionStore) =>
  (store.session ?? []).filter((session) => isRootVisibleSession(session, store.path.directory))

export const sortedRootSessions = (store: SessionStore, now: number) => roots(store).sort(sortSessions(now))

export const latestRootSession = (stores: SessionStore[], now: number) =>
  stores.flatMap(roots).sort(sortSessions(now))[0]

export const resolveAutoselectProject = (projects: Array<{ worktree: string }>, last?: string) => {
  if (projects.length === 0) return last
  return (projects.find((project) => project.worktree === last) ?? projects[0])?.worktree
}

export function hasProjectPermissions<T>(
  request: Record<string, T[] | undefined> | undefined,
  include: (item: T) => boolean = () => true,
) {
  return Object.values(request ?? {}).some((list) => list?.some(include))
}

export const childSessionOnPath = (sessions: Session[] | undefined, rootID: string, activeID?: string) => {
  const list = sessions ?? []
  if (activeID && activeID !== rootID) {
    const map = new Map(list.map((session) => [session.id, session]))
    let id = activeID

    while (id) {
      const session = map.get(id)
      if (!session?.parentID) return
      if (session.parentID === rootID) return session
      id = session.parentID
    }

    return
  }

  if (activeID !== rootID) return

  return list
    .filter((session) => session.parentID === rootID && !session.time?.archived)
    .filter((session) => session.metadata?.appChat === true || (session.title ?? "").startsWith("App: "))
    .sort((a, b) => (b.time.updated ?? b.time.created) - (a.time.updated ?? a.time.created))[0]
}

export const displayName = (project: { name?: string; worktree: string }) =>
  project.name || getFilename(project.worktree)

export const errorMessage = (err: unknown, fallback: string) => {
  if (err && typeof err === "object" && "data" in err) {
    const data = (err as { data?: { message?: string } }).data
    if (data?.message) return data.message
  }
  if (err instanceof Error) return err.message
  return fallback
}

export const effectiveWorkspaceOrder = (local: string, dirs: string[], persisted?: string[]) => {
  const root = pathKey(local)
  const live = new Map<string, string>()

  for (const dir of dirs) {
    const key = pathKey(dir)
    if (key === root) continue
    if (!live.has(key)) live.set(key, dir)
  }

  if (!persisted?.length) return [local, ...live.values()]

  const result = [local]
  for (const dir of persisted) {
    const key = pathKey(dir)
    if (key === root) continue
    const match = live.get(key)
    if (!match) continue
    result.push(match)
    live.delete(key)
  }

  return [...result, ...live.values()]
}
